import React, { useState, useEffect } from 'react';
import { Calendar, Home, Building2, Trash2, Plus, X, Check, AlertCircle } from 'lucide-react';
import api from '../services/api';

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const HybridScheduleModal = ({ isOpen, onClose, employee, onSaved }) => {
  const [weeklyDays, setWeeklyDays] = useState({
    Monday: 'OFFICE',
    Tuesday: 'OFFICE',
    Wednesday: 'OFFICE',
    Thursday: 'OFFICE',
    Friday: 'OFFICE',
    Saturday: 'OFFICE'
  });
  const [dateOverrides, setDateOverrides] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // New override form state
  const [overrideDate, setOverrideDate] = useState('');
  const [overrideType, setOverrideType] = useState('WFH');
  const [overrideReason, setOverrideReason] = useState('');

  useEffect(() => {
    if (isOpen && employee?._id) {
      loadSchedule();
    }
  }, [isOpen, employee]);

  const loadSchedule = async () => {
    setLoading(true);
    setError('');
    setSuccessMsg('');
    try {
      const res = await api.get(`/employees/${employee._id}/hybrid-schedule`);
      const sched = res.data || {};
      
      const loadedWeekly = sched.weeklyDays || {};
      const newWeekly = { ...weeklyDays };
      DAYS_OF_WEEK.forEach(day => {
        newWeekly[day] = loadedWeekly[day] || 'OFFICE';
      });
      setWeeklyDays(newWeekly);

      setDateOverrides(sched.dateOverrides || []);
    } catch (err) {
      console.error('Failed to load hybrid schedule', err);
      // Fallback to employee's embedded schedule if available
      if (employee.hybridSchedule) {
        const loadedWeekly = employee.hybridSchedule.weeklyDays || {};
        const newWeekly = { ...weeklyDays };
        DAYS_OF_WEEK.forEach(day => {
          newWeekly[day] = loadedWeekly[day] || 'OFFICE';
        });
        setWeeklyDays(newWeekly);
        setDateOverrides(employee.hybridSchedule.dateOverrides || []);
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !employee) return null;

  const handleDayToggle = (day, type) => {
    setWeeklyDays(prev => ({
      ...prev,
      [day]: type
    }));
  };

  const applyPreset = (preset) => {
    if (preset === 'MON_WED_WFH') {
      setWeeklyDays({
        Monday: 'WFH',
        Tuesday: 'OFFICE',
        Wednesday: 'WFH',
        Thursday: 'OFFICE',
        Friday: 'OFFICE',
        Saturday: 'OFFICE'
      });
    } else if (preset === 'TUE_THU_WFH') {
      setWeeklyDays({
        Monday: 'OFFICE',
        Tuesday: 'WFH',
        Wednesday: 'OFFICE',
        Thursday: 'WFH',
        Friday: 'OFFICE',
        Saturday: 'OFFICE'
      });
    } else if (preset === 'ALL_OFFICE') {
      setWeeklyDays({
        Monday: 'OFFICE',
        Tuesday: 'OFFICE',
        Wednesday: 'OFFICE',
        Thursday: 'OFFICE',
        Friday: 'OFFICE',
        Saturday: 'OFFICE'
      });
    } else if (preset === 'ALL_WFH') {
      setWeeklyDays({
        Monday: 'WFH',
        Tuesday: 'WFH',
        Wednesday: 'WFH',
        Thursday: 'WFH',
        Friday: 'WFH',
        Saturday: 'WFH'
      });
    }
  };

  const handleAddOverride = () => {
    if (!overrideDate) {
      setError('Please select a valid date for override.');
      return;
    }

    // Check if override for date already exists
    if (dateOverrides.some(o => o.date === overrideDate)) {
      setError(`An override for ${overrideDate} already exists. Remove it first to update.`);
      return;
    }

    setError('');
    setDateOverrides(prev => [
      ...prev,
      {
        date: overrideDate,
        type: overrideType,
        reason: overrideReason.trim()
      }
    ]);

    setOverrideDate('');
    setOverrideReason('');
  };

  const handleRemoveOverride = (dateToRemove) => {
    setDateOverrides(prev => prev.filter(o => o.date !== dateToRemove));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccessMsg('');
    try {
      await api.put(`/employees/${employee._id}/hybrid-schedule`, {
        weeklyDays,
        dateOverrides
      });
      setSuccessMsg('Hybrid schedule saved successfully! ✨');
      if (onSaved) onSaved();
      setTimeout(() => {
        onClose();
      }, 1200);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to save hybrid schedule');
    } finally {
      setSaving(false);
    }
  };

  const todayStr = new Date().toISOString().split('T')[0];

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden animate-fade-in-up border border-gray-100">
        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-blue-50/50 to-indigo-50/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-blue-600 text-white flex items-center justify-center font-bold shadow-md shadow-blue-500/20">
              <Calendar className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">
                Hybrid Schedule: {employee.firstName} {employee.lastName}
              </h3>
              <p className="text-xs text-gray-500">
                ID: {employee.employeeId} • Configure weekly work locations and specific date overrides
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-2 hover:bg-white rounded-xl transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content body */}
        <div className="p-6 overflow-y-auto space-y-6 flex-1 divide-y divide-gray-100">
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-2xl text-sm flex items-center gap-2">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {successMsg && (
            <div className="p-4 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-2xl text-sm flex items-center gap-2">
              <Check className="w-4 h-4 shrink-0 text-emerald-600" />
              <span>{successMsg}</span>
            </div>
          )}

          {/* Section 1: Weekly Recurring Days */}
          <div className="pt-2 first:pt-0">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
              <div>
                <h4 className="text-sm font-bold text-gray-800 uppercase tracking-wider">
                  Weekly Recurring Schedule
                </h4>
                <p className="text-xs text-gray-500">
                  Select where the employee is expected to work on each day of the week.
                </p>
              </div>
              <div className="flex flex-wrap gap-1.5">
                <button
                  type="button"
                  onClick={() => applyPreset('MON_WED_WFH')}
                  className="text-xs px-2.5 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition"
                  title="Mon & Wed WFH, rest Office"
                >
                  Mon/Wed WFH
                </button>
                <button
                  type="button"
                  onClick={() => applyPreset('TUE_THU_WFH')}
                  className="text-xs px-2.5 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition"
                  title="Tue & Thu WFH, rest Office"
                >
                  Tue/Thu WFH
                </button>
                <button
                  type="button"
                  onClick={() => applyPreset('ALL_OFFICE')}
                  className="text-xs px-2.5 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition"
                >
                  All Office
                </button>
              </div>
            </div>

            {loading ? (
              <div className="py-6 text-center text-sm text-gray-400">Loading schedule...</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {DAYS_OF_WEEK.map(day => {
                  const currentType = weeklyDays[day] || 'OFFICE';
                  const isWfh = currentType === 'WFH';
                  return (
                    <div
                      key={day}
                      className="flex items-center justify-between p-3 rounded-2xl border border-gray-100 bg-gray-50/50 hover:bg-gray-50 transition"
                    >
                      <span className="font-semibold text-sm text-gray-800">{day}</span>
                      <div className="flex items-center bg-white p-1 rounded-xl shadow-sm border border-gray-200 text-xs">
                        <button
                          type="button"
                          onClick={() => handleDayToggle(day, 'OFFICE')}
                          className={`flex items-center gap-1 px-3 py-1.5 rounded-lg font-semibold transition ${
                            !isWfh
                              ? 'bg-blue-600 text-white shadow-sm'
                              : 'text-gray-500 hover:text-gray-800'
                          }`}
                        >
                          <Building2 className="w-3.5 h-3.5" />
                          Office
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDayToggle(day, 'WFH')}
                          className={`flex items-center gap-1 px-3 py-1.5 rounded-lg font-semibold transition ${
                            isWfh
                              ? 'bg-purple-600 text-white shadow-sm'
                              : 'text-gray-500 hover:text-gray-800'
                          }`}
                        >
                          <Home className="w-3.5 h-3.5" />
                          WFH
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Section 2: Specific Date Overrides */}
          <div className="pt-6">
            <div className="mb-4">
              <h4 className="text-sm font-bold text-gray-800 uppercase tracking-wider">
                Specific Date Overrides
              </h4>
              <p className="text-xs text-gray-500">
                Override the regular weekly schedule for specific calendar dates whenever needed.
              </p>
            </div>

            {/* Add override form */}
            <div className="bg-gray-50 p-4 rounded-2xl border border-gray-100 mb-4">
              <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-end">
                <div className="sm:col-span-4">
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Select Date</label>
                  <input
                    type="date"
                    min={todayStr}
                    value={overrideDate}
                    onChange={(e) => setOverrideDate(e.target.value)}
                    className="w-full text-xs p-2.5 bg-white border border-gray-200 rounded-xl focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="sm:col-span-3">
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Work Location</label>
                  <select
                    value={overrideType}
                    onChange={(e) => setOverrideType(e.target.value)}
                    className="w-full text-xs p-2.5 bg-white border border-gray-200 rounded-xl focus:ring-blue-500 focus:border-blue-500 font-medium"
                  >
                    <option value="WFH">🏠 Work From Home</option>
                    <option value="OFFICE">🏢 Office</option>
                  </select>
                </div>
                <div className="sm:col-span-3">
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Note (Optional)</label>
                  <input
                    type="text"
                    placeholder="e.g. Client visit"
                    value={overrideReason}
                    onChange={(e) => setOverrideReason(e.target.value)}
                    className="w-full text-xs p-2.5 bg-white border border-gray-200 rounded-xl focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="sm:col-span-2">
                  <button
                    type="button"
                    onClick={handleAddOverride}
                    className="w-full py-2.5 px-3 bg-gray-900 hover:bg-black text-white text-xs font-bold rounded-xl shadow transition flex items-center justify-center gap-1"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Add
                  </button>
                </div>
              </div>
            </div>

            {/* Overrides list */}
            {dateOverrides.length === 0 ? (
              <p className="text-xs text-gray-400 italic text-center py-2">
                No specific date overrides added yet. Weekly schedule applies to all days.
              </p>
            ) : (
              <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                {dateOverrides.map((override) => (
                  <div
                    key={override.date}
                    className="flex items-center justify-between p-3 rounded-xl border border-gray-100 bg-white hover:border-gray-200 transition text-xs shadow-sm"
                  >
                    <div className="flex items-center gap-3">
                      <span className="font-mono font-bold text-gray-800">{override.date}</span>
                      <span
                        className={`px-2.5 py-1 rounded-full font-bold flex items-center gap-1 ${
                          override.type === 'WFH'
                            ? 'bg-purple-100 text-purple-700'
                            : 'bg-blue-100 text-blue-700'
                        }`}
                      >
                        {override.type === 'WFH' ? (
                          <>
                            <Home className="w-3 h-3" /> WFH
                          </>
                        ) : (
                          <>
                            <Building2 className="w-3 h-3" /> Office
                          </>
                        )}
                      </span>
                      {override.reason && (
                        <span className="text-gray-500 italic">"{override.reason}"</span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveOverride(override.date)}
                      className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition"
                      title="Remove override"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer actions */}
        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50/50 flex justify-end items-center gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-5 py-2.5 border border-gray-300 text-gray-700 text-sm font-semibold rounded-xl hover:bg-gray-100 transition"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || loading}
            className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold rounded-xl shadow-md shadow-blue-500/20 disabled:opacity-50 transition flex items-center gap-2"
          >
            {saving ? (
              <>
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                Saving...
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                Save Schedule
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default HybridScheduleModal;
